<h2 class="mb-3 mt-5 text-center text-2xl">AEM → Sanity Diff</h2>

<div class="mx-auto flex flex-row flex-wrap items-center gap-5">
  <div class="flex-none basis-[600px]">
    <mat-form-field subscriptSizing="dynamic" class="cursor-not-allowed">
      <mat-label>AEM Root Path</mat-label>

      <input
        matInput
        readonly
        [(ngModel)]="domainAemRootPath"
        autocomplete="off"
        class="cursor-not-allowed"
      />

      <div
        matSuffix
        class="flex flex-row items-center justify-center gap-1 pr-1"
      >
        @if (sitemapAem()) {
          <button
            mat-icon-button
            (click)="openAemMenuDialog()"
            class="mr-2"
            matTooltip="View Menu Structure"
            matTooltipPosition="above"
          >
            <fa-icon [icon]="['fas', 'bars']"></fa-icon>
          </button>

          <a
            mat-icon-button
            color="primary"
            [href]="backupAwsAem()"
            target="_blank"
            class="mr-2"
            matTooltip="View files in AWS"
            matTooltipPosition="above"
          >
            <fa-icon [icon]="['fas', 'folder-tree']"></fa-icon>
          </a>

          <a
            mat-icon-button
            color="primary"
            [href]="sitemapAem()"
            target="_blank"
            class="mr-2"
            matTooltip="View AEM backup sitemap"
            matTooltipPosition="above"
          >
            <fa-icon [icon]="['fas', 'sitemap']"></fa-icon>
          </a>
        }

        <button
          mat-icon-button
          (click)="getClientAEMPath()"
          class="mr-2"
          matTooltip="Search for AEM path"
          matTooltipPosition="above"
        >
          <fa-icon [icon]="['fas', 'search']"></fa-icon>
        </button>
      </div>
    </mat-form-field>
  </div>

  <div class="flex-1 basis-[800px]">
    <mat-form-field subscriptSizing="dynamic">
      <mat-label>AEM Page Path</mat-label>

      <input matInput [(ngModel)]="domainAemCurrentPath" autocomplete="off" />
    </mat-form-field>
  </div>

  <div class="flex-1 basis-[800px]">
    <mat-form-field subscriptSizing="dynamic">
      <mat-label>Site URL</mat-label>

      <input matInput [(ngModel)]="domainHdUrl" autocomplete="off" />

      <div
        matSuffix
        class="flex flex-row items-center justify-center gap-1 pr-1"
      >
        @if (sitemapHd()) {
          <a
            mat-icon-button
            matIconSuffix
            color="primary"
            [href]="sitemapHd()"
            target="_blank"
            class="mr-2"
            matTooltip="View live sitemap"
            matTooltipPosition="above"
          >
            <fa-icon [icon]="['fas', 'sitemap']"></fa-icon>
          </a>
        }

        <button
          mat-icon-button
          matIconSuffix
          (click)="getClientAEMPath(true)"
          class="mr-2"
          matTooltip="Search for HD domain"
          matTooltipPosition="above"
        >
          <fa-icon [icon]="['fas', 'search']"></fa-icon>
        </button>
      </div>
    </mat-form-field>
  </div>

  <div class="flex-none basis-[200px]">
    <button
      mat-raised-button
      color="accent"
      (click)="navigate()"
      class="h-auto w-full py-3"
    >
      Load Pages
    </button>
  </div>
</div>

<div
  class="mt-8 flex flex-wrap items-stretch justify-center gap-5 overflow-hidden"
>
  <div class="flex flex-grow basis-[600px] flex-col overflow-hidden">
    @if (loadedAemFullPath()) {
      <h3 class="mb-3 mt-5 text-center text-xl">
        {{ loadedAemFullPath() }}
      </h3>

      @let aemJson = loadedAemJson.value();

      @if (aemJson) {
        <table class="mb-4 [&_td]:px-2 [&_td]:py-1">
          <tbody>
            <tr>
              <td>Title</td>
              <td>{{ aemJson["pageTitle"] }}</td>
            </tr>
            <tr>
              <td>Desc</td>
              <td>{{ aemJson["jcr:description"] }}</td>
            </tr>
          </tbody>
        </table>

        <div class="flex-grow bg-white p-4 text-black [&_p_a]:underline">
          <ng-component
            [ngTemplateOutlet]="aemComponent"
            [ngTemplateOutletContext]="{ component: aemJson.root }"
          ></ng-component>
        </div>
      } @else if (aemJson === null) {
        <mat-toolbar class="placeholder">
          That AEM path does not exist.
        </mat-toolbar>
      } @else {
        <div class="flex justify-center">
          <mat-progress-spinner mode="indeterminate" diameter="50" />
        </div>
      }
    } @else {
      <mat-toolbar class="placeholder"> No AEM path loaded. </mat-toolbar>
    }
  </div>

  <div class="flex flex-grow basis-[600px] flex-col overflow-hidden">
    @if (loadedDomainHdUrl() && loadedDomainHdUrlString()) {
      <h3 class="mb-3 mt-5 text-center text-xl">
        {{ loadedDomainHdUrlString() }}
      </h3>

      <div class="flex-grow">
        <iframe
          [src]="loadedDomainHdUrl()"
          class="h-full min-h-[800px] w-full bg-white"
        ></iframe>
      </div>
    } @else {
      <mat-toolbar class="placeholder"> No site loaded. </mat-toolbar>
    }
  </div>
</div>

<ng-template
  #aemComponent
  let-component="component"
  let-manualType="manualType"
  let-renderSelf="renderSelf"
>
  @let renderingComponents =
    renderSelf ? [component] : getChildComponents(component);

  @for (subComponent of renderingComponents; track $index) {
    @switch (manualType ?? subComponent["_cmm_component_type"]) {
      @case ("container") {
        <div
          class="container my-10 flex flex-wrap items-stretch gap-5 [&_>div]:flex-shrink-0 [&_>div]:flex-grow [&_>div]:basis-[300px] [&_ng-component]:hidden"
        >
          <ng-component
            [ngTemplateOutlet]="aemComponent"
            [ngTemplateOutletContext]="{ component: subComponent }"
          ></ng-component>
        </div>
      }
      @case ("secured") {
        <div class="flex flex-wrap gap-10 [&_ng-component]:hidden">
          <div class="my-10 flex flex-wrap items-center gap-5">
            <ng-component
              [ngTemplateOutlet]="aemComponent"
              [ngTemplateOutletContext]="{ component: subComponent }"
            ></ng-component>
          </div>
        </div>
      }
      @case ("responsivegrid") {
        <div class="">
          <ng-component
            [ngTemplateOutlet]="aemComponent"
            [ngTemplateOutletContext]="{ component: subComponent }"
          ></ng-component>
        </div>
      }
      @case ("googlemaps") {
        <div
          class="text-gray flex min-h-[200px] min-w-[300px] items-center justify-center bg-gray-400 italic"
        >
          Google Map
        </div>
      }
      @case ("teaser") {
        <div class="flex flex-col justify-center gap-5 p-5 text-center">
          @if (subComponent["fileReference"]) {
            <div>
              <img [src]="CDN_AEM_ROOT + subComponent['fileReference']" />
            </div>
          }
          <h3>{{ subComponent["jcr:title"] }}</h3>
          <div [innerHTML]="subComponent['jcr:description']"></div>
          @if (subComponent["actionsEnabled"] === "true") {
            <div class="flex justify-center">
              <a
                [href]="subComponent['actions']['item0']['link']"
                target="_blank"
                class="rounded-full border border-black/60 bg-white px-3 py-1 text-black"
              >
                {{ subComponent["actions"]["item0"]["text"] }}
              </a>
            </div>
          }
        </div>
      }
      @case ("title") {
        <div
          class="basis-full text-center font-medium"
          [style]="getFontSize(subComponent['type'])"
        >
          {{ subComponent["jcr:title"] }}
        </div>
      }
      @case ("experiencefragment") {
        <div class="basis-full text-center italic opacity-80">
          {{ subComponent["fragmentVariationPath"] }}
        </div>
      }
      @case ("carousel") {
        <div
          class="flex basis-[300px] flex-wrap gap-5 [&_>div]:flex-grow [&_ng-component]:hidden"
        >
          <div class="basis-full p-7 text-center">
            <div class="text-lg">{{ subComponent["overlayTitle"] }}</div>

            <a
              [href]="subComponent['overlayButtonLink']"
              target="_blank"
              class="rounded-full border border-black/60 bg-white px-3 py-1 text-black"
            >
              {{ subComponent["overlayButtonTitle"] }}
            </a>
          </div>

          @for (
            image of getChildComponents(subComponent, ["item"]);
            track $index
          ) {
            <ng-component
              [ngTemplateOutlet]="aemComponent"
              [ngTemplateOutletContext]="{
                component: image,
                manualType: 'image',
                renderSelf: true,
              }"
            ></ng-component>
          }
        </div>
      }
      @case ("image") {
        @if (subComponent["fileReference"]) {
          <div
            class="p-5 italic text-black/80"
            style="overflow-wrap: anywhere"
            [ngClass]="{
              'flex items-center justify-center bg-gray-400':
                subComponent['fileReference'].includes('/us/master/'),
            }"
          >
            @if (!subComponent["fileReference"].includes("/us/master/")) {
              <img [src]="CDN_AEM_ROOT + subComponent['fileReference']" />

              <div>
                @let dynamicData = dynamicFetchedData();

                <strong>alt:</strong>
                {{
                  (subComponent["altValueFromDAM"] ?? true)
                    ? (dynamicData[subComponent["fileReference"]] ??
                      fetchAltText(subComponent["fileReference"]))
                    : subComponent["alt"]
                }}
              </div>
            }

            <div>
              <strong>file:</strong>
              {{ subComponent["fileReference"] }}
            </div>
          </div>
        } @else {
          <div>
            <strong>[[ Image does not have a file reference ]]</strong>
          </div>
        }
      }
      @case ("embed") {
        @if (subComponent["type"] === "html") {
          <div [innerHTML]="updateRelativeSrc(subComponent['html'])"></div>
        } @else if (subComponent["youtubeVideoId"]) {
          <div>
            <a
              [href]="
                'https://www.youtube.com/watch?v=' +
                subComponent['youtubeVideoId']
              "
              target="_blank"
            >
              <img
                class="block"
                [src]="
                  'https://img.youtube.com/vi/' +
                  subComponent['youtubeVideoId'] +
                  '/hqdefault.jpg'
                "
              />
            </a>

            <div class="text-center italic text-black/80">
              <strong>Video ID:</strong> {{ subComponent["youtubeVideoId"] }}
            </div>
          </div>
        }
      }
      @case ("imagegallery") {
        @if (subComponent["galleryAssetsRootPath"]) {
          <div class="grid grid-cols-fill-48 gap-4">
            @let dynamicData = dynamicFetchedData();

            @for (
              image of dynamicData[subComponent["galleryAssetsRootPath"]];
              track $index
            ) {
              <div>
                <img
                  [src]="CDN_AEM_ROOT + image"
                  class="aspect-square object-contain"
                  loading="lazy"
                />

                <div>
                  <strong>alt:</strong>
                  {{ dynamicData[image] ?? fetchAltText(image) }}
                </div>

                <div>
                  <strong>file:</strong>
                  {{ image }}
                </div>
              </div>
            }

            @if (!dynamicData[subComponent["galleryAssetsRootPath"]]) {
              {{ fetchGalleryImages(subComponent["galleryAssetsRootPath"]) }}
            }
          </div>
        }
      }
      @case ("separator") {
        <hr class="my-5 border-t border-black/70" />
      }
      @case ("text") {
        <div [innerHTML]="subComponent['text'] ?? ''"></div>
      }
      @case ("googlereviews") {
        <div>
          <div class="basis-full text-center text-lg font-medium">
            {{ subComponent["googleReviewsTitle"] }}
          </div>
          <div
            class="mt-3 flex min-h-[120px] items-center justify-center bg-gray-400 italic"
          >
            Reviews
          </div>
        </div>
      }
      @case ("yelpreviews") {
        <div>
          <div class="basis-full text-center text-lg font-medium">
            {{ subComponent["yelpReviewsTitle"] }}
          </div>
          <div
            class="mt-3 flex min-h-[120px] items-center justify-center bg-gray-400 italic"
          >
            Reviews
          </div>
        </div>
      }
      @case ("button") {
        <div class="flex justify-center">
          <a
            [href]="subComponent['link']"
            target="_blank"
            class="rounded-full border border-black/60 bg-white px-3 py-1 text-black"
          >
            {{ subComponent["jcr:title"] }}
          </a>
        </div>
      }
    }
  }
</ng-template>

<ng-template #menuPage let-page="page">
  <div
    class="py-2"
    [matTooltip]="'Not In Menu'"
    matTooltipPosition="left"
    [matTooltipDisabled]="!page.hiddenInMenu"
  >
    <div class="text-lg">{{ page.title }}</div>
    <div class="text-sm">{{ page.path }}</div>
  </div>
</ng-template>

<ng-template #aemMenu>
  @if (menuDataSource.data.length === 0) {
    <div class="flex justify-center">
      <mat-progress-spinner mode="indeterminate" diameter="50" />
    </div>
  }

  <mat-tree [dataSource]="menuDataSource" [treeControl]="menuTreeControl">
    <mat-nested-tree-node
      #treeNode
      *matTreeNodeDef="let page"
      matTreeNodeToggle
      [ngClass]="{ 'opacity-65': page.hiddenInMenu }"
    >
      <ng-container
        *ngTemplateOutlet="menuPage; context: { page: page }"
      ></ng-container>
    </mat-nested-tree-node>

    <mat-nested-tree-node
      #nestedTreeNode
      *matTreeNodeDef="let page; when: hasMenuSubpages"
      [ngClass]="{ 'opacity-65': page.hiddenInMenu }"
    >
      <div class="mat-tree-node relative">
        <button matTreeNodeToggle [attr.aria-label]="'Toggle ' + page.slug">
          <fa-icon
            [icon]="[
              'fas',
              menuTreeControl.isExpanded(page)
                ? 'chevron-down'
                : 'chevron-right',
            ]"
            class="[&_svg]:w-5"
          ></fa-icon>
        </button>

        <div class="ml-2">
          <ng-container
            *ngTemplateOutlet="menuPage; context: { page: page }"
          ></ng-container>
        </div>
      </div>

      <div
        class="pb-4"
        role="group"
        [ngClass]="{
          hidden: !menuTreeControl.isExpanded(page),
          'opacity-65': page.hiddenInMenu,
        }"
      >
        <ng-container matTreeNodeOutlet></ng-container>
      </div>
    </mat-nested-tree-node>
  </mat-tree>
</ng-template>
